<template>
    <pc-loader v-bind:loading="isLoading">
        <pc-alert
            v-bind:severity="ALERT_SEVERITY.info"
            v-bind:shown="!isAtLeast(null, BP.md)">
            <div>{{ resolutionTooSmallWarning }}</div>
        </pc-alert>
        <div
            v-if="!importSuccess"
            class="row w-100">
            <pc-file
                class="col-xl-6 col-lg-8 col-12"
                v-bind:extensions="['csv']"
                show-extensions
                v-bind:upload-function="uploadFile"
                v-bind:label="uploadFileLabel" />
        </div>
        <pc-alert
            class="mt-2"
            v-bind:severity="ALERT_SEVERITY.error"
            v-bind:shown="hasErrorMessages"
            v-bind:title="unableToImportAlertHeader">
            <div>
                <div
                    v-for="(errorMessage, index) in importResult.ErrorMessages"
                    v-bind:key="index">
                    {{ errorMessage }}
                </div>
            </div>
        </pc-alert>
        <pc-alert
            v-bind:severity="ALERT_SEVERITY.warning"
            v-bind:shown="importSuccess && warningMessages.length > 0"
            v-bind:title="warningAlertHeader">
            <div>
                <div
                    v-for="(warningMessage, index) in warningMessages"
                    v-bind:key="index">
                    {{ warningMessage }}
                </div>
            </div>
        </pc-alert>
        <div v-if="importSuccess">
            <pc-label v-bind:label="tablePreviewLabel" />
            <pc-table
                v-bind:items="previewItems"
                v-bind:columns="tableColumns"
                v-bind:empty-text="emptyTableText"
                v-bind:hide-search="true"
                display-table-when-empty
                no-card-view
                key-prop="index">
                <template
                    v-for="col in tableColumns"
                    v-slot:[col.property]="row"
                    v-bind:key="col.property">
                    <span v-bind:class="{ isImported: row.isImported }">
                        {{ row[col.property] }}
                    </span>
                </template>
            </pc-table>
        </div>
    </pc-loader>
</template>

<script setup>
    import { PROJECT_FORM_CONTROLLER_TYPES } from "Forms/Components/ProjectForm/projectFormControllerStore";
    import { inject, ref, computed } from "vue";
    import { PC_RES_SYMBOL } from "_Main/injectionSymbols";
    import { ALERT_SEVERITY } from "Util/alerts";
    import { isAtLeast } from "Responsive/manager";
    import { BP } from "Responsive/breakpoint";
    import { DIALOG_RESULT } from "Components/pc-dialog.vue";
    import { useResource } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/importNewRowsDialog.i18n";
    import { KEYS as TABLE_KEYS, NAMESPACE as TABLE_NAMESPACE } from "../i18n/table.i18n";

    const props = defineProps({
        elementId: { type: String, required: true },
        tableItems: { type: [Array, Function], required: true },
        tableColumns: { type: Array, required: true },
        maxTableLength: { type: Number, required: true }
    });

    const res = inject(PC_RES_SYMBOL);
    const isLoading = ref(false);
    const importResult = ref(null);
    const {
        uploadFileLabel,
        unableToImportAlertHeader,
        warningAlertHeader,
        tablePreviewLabel,
        tooManyRowsError,
        resolutionTooSmallWarning
    } = useResource(KEYS, NAMESPACE);
    const { emptyTableText } = useResource(TABLE_KEYS, TABLE_NAMESPACE);

    const previewItems = computed(() => {
        const previewItems = props.tableItems.map((item) => {
            item.isImported = false;
            return item;
        });

        if (importResult.value && importResult.value.TableColumns && importResult.value.TableColumns.length) {
            const colCount = importResult.value.TableColumns.length;
            const rowCount = importResult.value.TableColumns[0].Values.length;

            for (let i = 0; i < rowCount; i++) {
                const importedRow = { isImported: true, index: previewItems.length + 1 };
                for (let j = 0; j < colCount; j++) {
                    const importedCol = importResult.value.TableColumns[j];
                    importedRow[importedCol.DataFieldId] = importedCol.Values[i];
                }
                previewItems.push(importedRow);
            }
        }

        return previewItems;
    });

    const warningMessages = computed(() => {
        const warningMessages = [];

        for (let i = 1; i < props.tableColumns.length; i++) {
            const columnDefinition = props.tableColumns[i];
            if (!columnDefinition.showRequired) {
                continue;
            }

            // check if there are required values missing in the imported items
            for (let j = props.tableItems.length; j < previewItems.value.length; j++) {
                if (!previewItems.value[j][columnDefinition.property]) {
                    warningMessages.push(
                        res({
                            namespace: NAMESPACE,
                            key: KEYS.missingRequiredValueMessage,
                            values: { headerName: columnDefinition.header }
                        })
                    );
                    break;
                }
            }
        }
        return warningMessages;
    });

    const importSuccess = computed(() => {
        if (importResult.value && !(importResult.value.ErrorMessages && importResult.value.ErrorMessages.length)) {
            return true;
        }
        return false;
    });

    const hasErrorMessages = computed(() => {
        if (importResult.value && importResult.value.ErrorMessages && importResult.value.ErrorMessages.length) {
            return true;
        }
        return false;
    });

    async function uploadFile(file) {
        isLoading.value = true;
        const uploadResult = await this.$store.dispatch(
            PROJECT_FORM_CONTROLLER_TYPES.actions.uploadTableFile,
            { file, formElementId: props.elementId },
            { root: true }
        );
        if (
            uploadResult.TableColumns &&
            uploadResult.TableColumns[0].Values.length + props.tableItems.length > props.maxTableLength
        ) {
            if (!uploadResult.ErrorMessages) {
                uploadResult.ErrorMessages = [];
            }
            uploadResult.ErrorMessages.push(tooManyRowsError.value);
        }
        importResult.value = uploadResult;
        isLoading.value = false;
    }

    async function isValidToClose(dialogResult) {
        switch (dialogResult) {
            case DIALOG_RESULT.ok:
                return importSuccess.value;
            case DIALOG_RESULT.cancel:
                return true;
            default:
                return false;
        }
    }

    const dialogResult = () => importResult.value;

    defineExpose({
        isValidToClose,
        dialogResult
    });
</script>

<style lang="scss" scoped>
    .isImported {
        font-weight: bold;
        font-style: italic;
    }
</style>
